import { Button as MuiButton, type ButtonProps } from '@mui/material';
import { styled } from '@mui/material/styles';

/**
 * Styled Button component with consistent design patterns
 * Provides default styling while maintaining full MUI Button API compatibility
 */
export const StyledButton = styled(MuiButton)<ButtonProps>(({ theme, variant, color }) => ({
  borderRadius: theme.spacing(1),
  textTransform: 'none',

  // Apply background and elevation to outlined variant for better visibility
  ...(variant === 'outlined' && {
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 2px 4px -2px rgba(0, 0, 0, 0.08), 0 4px 8px -2px rgba(0, 0, 0, 0.04)', // Subtle custom shadow
    '&:hover': {
      boxShadow: '0 2px 4px -2px rgba(0, 0, 0, 0.12), 0 4px 8px -2px rgba(0, 0, 0, 0.08)', // Slightly more defined on hover
    },

    // Neutral color styling for outlined variant
    ...(color === 'inherit' && {
      borderColor: theme.palette.grey[300],
      color: theme.palette.grey[700],
      '&:hover': {
        borderColor: theme.palette.grey[400],
        backgroundColor: theme.palette.grey[50],
        boxShadow: '0 2px 4px -2px rgba(0, 0, 0, 0.12), 0 4px 8px -2px rgba(0, 0, 0, 0.08)', // Same hover shadow
      },
      '&:active': {
        backgroundColor: theme.palette.grey[100],
      },
    }),
  }),

  // Neutral color styling for text variant
  ...(variant === 'text' &&
    color === 'inherit' && {
      color: theme.palette.grey[700],
      '&:hover': {
        backgroundColor: theme.palette.grey[50],
      },
    }),

  // Neutral color styling for contained variant
  ...(variant === 'contained' &&
    color === 'inherit' && {
      backgroundColor: theme.palette.grey[200],
      color: theme.palette.grey[800],
      '&:hover': {
        backgroundColor: theme.palette.grey[300],
      },
    }),
})) as typeof MuiButton;
